import logging from '@sstdev/lib_logging';
import PropTypes from 'prop-types';
import { createContext, createElement as rc, useCallback, useMemo, useReducer } from 'react';
import {
    ACTIONS,
    SCREEN_STATES,
    continuousScanReducer,
    getInitialState,
    isValidTransition
} from './continuousScanStateMachine';

/**
 * Context for continuous scan state management
 * Provides:
 * - Current screen state
 * - Dispatch actions for screen transitions
 * - Shared data (counts, summary, event history)
 */
export const ContinuousScanContext = createContext();

/**
 * Provider component for continuous scan state
 */
export function ContinuousScanContextProvider({ children, onClose }) {
    const [state, dispatch] = useReducer(continuousScanReducer, {}, getInitialState);

    // Action dispatchers with validation and logging
    const pause = useCallback(() => {
        logging.debug('[ContinuousScan] Action: PAUSE');
        if (isValidTransition(state.currentScreen, ACTIONS.PAUSE)) {
            dispatch({ type: ACTIONS.PAUSE });
        }
    }, [state.currentScreen]);

    const resume = useCallback(() => {
        logging.debug('[ContinuousScan] Action: RESUME');
        if (isValidTransition(state.currentScreen, ACTIONS.RESUME)) {
            dispatch({ type: ACTIONS.RESUME });
        }
    }, [state.currentScreen]);

    const selectCategory = useCallback((category) => {
        logging.debug('[ContinuousScan] Action: SELECT_CATEGORY', category);
        logging.debug('[ContinuousScan] Current screen:', state.currentScreen);
        logging.debug('[ContinuousScan] Valid transition?', isValidTransition(state.currentScreen, ACTIONS.SELECT_CATEGORY));
        if (isValidTransition(state.currentScreen, ACTIONS.SELECT_CATEGORY)) {
            logging.debug('[ContinuousScan] Dispatching SELECT_CATEGORY with category:', category);
            dispatch({ type: ACTIONS.SELECT_CATEGORY, payload: { category } });
        } else {
            logging.warn('[ContinuousScan] Invalid transition from', state.currentScreen, 'for SELECT_CATEGORY');
        }
    }, [state.currentScreen]);

    const closeCategoryDetails = useCallback(() => {
        logging.debug('[ContinuousScan] Action: CLOSE_CATEGORY_DETAILS');
        if (isValidTransition(state.currentScreen, ACTIONS.CLOSE_CATEGORY_DETAILS)) {
            dispatch({ type: ACTIONS.CLOSE_CATEGORY_DETAILS });
        }
    }, [state.currentScreen]);

    const closeScan = useCallback(() => {
        logging.debug('[ContinuousScan] Action: CLOSE_SCAN');
        if (isValidTransition(state.currentScreen, ACTIONS.CLOSE_SCAN)) {
            dispatch({ type: ACTIONS.CLOSE_SCAN });
            if (onClose) onClose();
        }
    }, [state.currentScreen, onClose]);

    const submitScan = useCallback(async () => {
        logging.debug('[ContinuousScan] Action: SUBMIT');
        if (isValidTransition(state.currentScreen, ACTIONS.SUBMIT)) {
            dispatch({ type: ACTIONS.SUBMIT });
            // Keep submitting true until completion
        }
    }, [state.currentScreen]);

    const updateSummary = useCallback((summary) => {
        logging.debug('[ContinuousScan] Action: UPDATE_SUMMARY');
        dispatch({ type: ACTIONS.UPDATE_SUMMARY, payload: { summary } });
    }, []);

    const updateCount = useCallback((counts) => {
        logging.debug('[ContinuousScan] Action: UPDATE_COUNT', counts);
        dispatch({ type: ACTIONS.UPDATE_COUNT, payload: { counts } });
    }, []);

    const updateConnectionStatus = useCallback((status) => {
        logging.debug('[ContinuousScan] Action: UPDATE_CONNECTION_STATUS', status);
        dispatch({ type: ACTIONS.UPDATE_CONNECTION_STATUS, payload: { status } });
    }, []);

    const removeEntry = useCallback((entryData) => {
        logging.debug('[ContinuousScan] Action: REMOVE_ENTRY', entryData);
        if (isValidTransition(state.currentScreen, ACTIONS.REMOVE_ENTRY)) {
            dispatch({ type: ACTIONS.REMOVE_ENTRY, payload: entryData });
        }
    }, [state.currentScreen]);

    const restoreEntry = useCallback((entryData) => {
        logging.debug('[ContinuousScan] Action: RESTORE_ENTRY', entryData);
        if (isValidTransition(state.currentScreen, ACTIONS.RESTORE_ENTRY)) {
            dispatch({ type: ACTIONS.RESTORE_ENTRY, payload: entryData });
        }
    }, [state.currentScreen]);

    // The backend classifies reads in ~1s batches (silo/stream's BATCH_WINDOW_MS) and publishes
    // its cumulative found/moved/unknown/error counts as it catches up - readCounts.total (the
    // reader's own live, monotonic physical count, see continuousScanEventParsing.js) can run
    // ahead of that for several batches after a fast scan. duplicateCount is deliberately excluded:
    // duplicate reads never increment readCounts.total either (see getNextReadCountsFromEvent),
    // so comparing against it here would never let isReconciling become false.
    // manuallyRemovedCount folds in rows the user explicitly removed via REMOVE_ENTRY - those lower
    // summaryData's counts the same way backend lag would, but they are a deliberate exclusion, not
    // something still in flight, so they must not count against reconciliation (see
    // continuousScanStateMachine.js's REMOVE_ENTRY case).
    const backendProcessedTotal =
        (state.summaryData?.foundCount || 0) +
        (state.summaryData?.movedCount || 0) +
        (state.summaryData?.unknownCount || 0) +
        (state.summaryData?.errorCount || 0) +
        (state.manuallyRemovedCount || 0);
    // summaryData is also reconciling-by-definition while it is still native's own placeholder
    // guess (see ContinuousScanScreen.native.js's continuousScanSummary handler and
    // ContinuousScanModule.java's buildHoldSummary()) - that guess's foundCount is the same
    // session-scoped unique-tag count that drives readCounts.total, so the count comparison below
    // would read 0 pending and miss exactly the window this is meant to catch: the real batch has
    // not landed yet, so the placeholder's numbers (e.g. "175") are what's on screen and about to
    // be corrected once it does.
    const isReconciling =
        state.currentScreen !== SCREEN_STATES.SCANNING &&
        (Boolean(state.summaryData?.isPlaceholder) || backendProcessedTotal < (state.readCounts.total || 0));
    // isReconciling/pendingReconciliationCount are informational only - Submit is never blocked on
    // them. A hard block here has no safe way to resolve when a read is permanently lost (backend
    // drops it because the session already ended, an MQTT publish fails, or it never leaves
    // native's buffer before pause) - Cancel (which discards the whole session) would be the only
    // way out. Screens show "Finalizing…" while this is true but stay submittable throughout.
    const pendingReconciliationCount = Math.max(0, (state.readCounts.total || 0) - backendProcessedTotal);

    // Memoize context value
    const contextValue = useMemo(
        () => ({
            // State
            currentScreen: state.currentScreen,
            selectedCategory: state.selectedCategory,
            readCounts: state.readCounts,
            summaryData: state.summaryData,
            removedItems: state.removedItems,
            isSubmitting: state.isSubmitting,
            eventHistory: state.eventHistory,
            isReconciling,
            pendingReconciliationCount,
            connectionStatus: state.connectionStatus,

            // Screen state constants
            SCREEN_STATES,

            // Actions
            pause,
            resume,
            selectCategory,
            closeCategoryDetails,
            closeScan,
            submitScan,
            updateSummary,
            updateCount,
            updateConnectionStatus,
            removeEntry,
            restoreEntry,

            // Raw dispatch for advanced use
            dispatch
        }),
        [
            state,
            isReconciling,
            pendingReconciliationCount,
            pause,
            resume,
            selectCategory,
            closeCategoryDetails,
            closeScan,
            submitScan,
            updateSummary,
            updateCount,
            updateConnectionStatus,
            removeEntry,
            restoreEntry
        ]
    );

    return rc(ContinuousScanContext.Provider, { value: contextValue }, children);
}

ContinuousScanContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func
};

export default ContinuousScanContextProvider;
